import { useState } from 'react';
import { ArrowDown, ArrowUp, GitBranch, Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ALLOWED_CONDITIONS } from '../../lib/surveyBranching';
import type {
  ChoiceOption,
  QuestionConfig,
  QuestionStep,
  SurveyLinkMode,
  SurveyStep,
} from '../../types/survey';
import { FieldLabel, LabeledInput, LabeledNumber, MarkdownField } from './fields';
import { STEP_TYPE_LABEL, branchingSummary } from './stepSummary';
import { BranchingEditor } from './BranchingEditor';

// Phase 6B B4 — per-type step editors, in a single focused modal. Edits are
// live (each change calls onChange) so the compose autosave persists them; the
// modal has no separate Save. Branching editing ("Edit branching") lands in B5.

export function StepEditorModal({
  step,
  allSteps,
  linkMode,
  onChange,
  onClose,
}: {
  step: SurveyStep;
  allSteps: SurveyStep[];
  linkMode: SurveyLinkMode;
  onChange: (next: SurveyStep) => void;
  onClose: () => void;
}) {
  const [editingBranching, setEditingBranching] = useState(false);

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-base font-semibold text-slate-800">Edit {STEP_TYPE_LABEL[step.type]}</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {step.kind === 'content' ? (
            <ContentEditor step={step} onChange={onChange} />
          ) : step.type === 'name' ? (
            <NameEditor step={step} />
          ) : (
            <QuestionEditor step={step} onChange={onChange} onEditBranching={() => setEditingBranching(true)} />
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 px-5 py-3">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>

      {editingBranching && step.kind === 'question' && (
        <BranchingEditor
          step={step}
          allSteps={allSteps}
          linkMode={linkMode}
          onSave={(next) => {
            onChange(next);
            setEditingBranching(false);
          }}
          onClose={() => setEditingBranching(false)}
        />
      )}
    </div>
  );
}

// ---------- content_page ----------
function ContentEditor({ step, onChange }: { step: Extract<SurveyStep, { kind: 'content' }>; onChange: (s: SurveyStep) => void }) {
  const set = <K extends keyof typeof step>(key: K, v: (typeof step)[K]) => onChange({ ...step, [key]: v });
  return (
    <>
      <LabeledInput id="cp-title" label="Page title" value={step.title} onChange={(v) => set('title', v)} placeholder="An update from your team" />
      <MarkdownField id="cp-body" label="Body" value={step.bodyMarkdown} onChange={(v) => set('bodyMarkdown', v)} rows={8} />
      <LabeledInput id="cp-image" label="Image URL (optional)" value={step.imageUrl ?? ''} onChange={(v) => set('imageUrl', v.trim() === '' ? null : v)} placeholder="https://…" />
      <LabeledInput id="cp-cta" label="Button label" value={step.ctaLabel} onChange={(v) => set('ctaLabel', v)} placeholder="Next" />
    </>
  );
}

// ---------- name (read-only system question) ----------
function NameEditor({ step }: { step: QuestionStep }) {
  return (
    <div className="space-y-2">
      <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
        This is a system question added automatically for per-location surveys. It captures who
        responded and can't be edited or removed. Switch to per-recipient mode (Send tab) to remove it.
      </p>
      <div>
        <FieldLabel>Prompt</FieldLabel>
        <p className="text-sm text-slate-800">{step.prompt}</p>
      </div>
    </div>
  );
}

// ---------- shared question fields + per-type config ----------
function QuestionEditor({
  step,
  onChange,
  onEditBranching,
}: {
  step: QuestionStep;
  onChange: (s: SurveyStep) => void;
  onEditBranching: () => void;
}) {
  const setStep = <K extends keyof QuestionStep>(key: K, v: QuestionStep[K]) => onChange({ ...step, [key]: v });
  const setConfig = (config: QuestionConfig) => onChange({ ...step, config });
  // Branching is offered only for types the engine accepts conditions on
  // (R-G: date/name carry none).
  const canBranch = ALLOWED_CONDITIONS[step.type].length > 0;

  return (
    <>
      <LabeledInput id="q-prompt" label="Question" value={step.prompt} onChange={(v) => setStep('prompt', v)} placeholder="What would you like to ask?" />
      <LabeledInput id="q-desc" label="Helper text (optional)" value={step.description ?? ''} onChange={(v) => setStep('description', v.trim() === '' ? null : v)} placeholder="Extra context shown below the question" />

      <ConfigEditor config={step.config} onChange={setConfig} />

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={step.required} onChange={(e) => setStep('required', e.target.checked)} />
        Required — recipients must answer before continuing
      </label>

      {canBranch && (
        <div className="rounded-md border border-slate-200 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Branching</p>
              <p className="text-xs text-slate-400">{branchingSummary(step)}</p>
            </div>
            <Button variant="outline" size="sm" onClick={onEditBranching}>
              <GitBranch className="h-4 w-4" />
              Edit branching
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function ConfigEditor({ config, onChange }: { config: QuestionConfig; onChange: (c: QuestionConfig) => void }) {
  switch (config.type) {
    case 'yes_no':
      return (
        <div className="grid grid-cols-2 gap-4">
          <LabeledInput label="“Yes” label (optional)" value={config.trueLabel ?? ''} onChange={(v) => onChange({ ...config, trueLabel: v || undefined })} placeholder="Yes" />
          <LabeledInput label="“No” label (optional)" value={config.falseLabel ?? ''} onChange={(v) => onChange({ ...config, falseLabel: v || undefined })} placeholder="No" />
        </div>
      );
    case 'multiple_choice_single':
      return (
        <>
          <OptionsEditor options={config.options} onChange={(options) => onChange({ ...config, options })} />
          <AllowOtherToggle value={config.allowOther ?? false} onChange={(allowOther) => onChange({ ...config, allowOther })} />
        </>
      );
    case 'multiple_choice_multi':
      return (
        <>
          <OptionsEditor options={config.options} onChange={(options) => onChange({ ...config, options })} />
          <AllowOtherToggle value={config.allowOther ?? false} onChange={(allowOther) => onChange({ ...config, allowOther })} />
          <div className="grid grid-cols-2 gap-4">
            <LabeledNumber label="Min selections (optional)" value={config.minSelections ?? null} onChange={(v) => onChange({ ...config, minSelections: v ?? undefined })} min={0} />
            <LabeledNumber label="Max selections (optional)" value={config.maxSelections ?? null} onChange={(v) => onChange({ ...config, maxSelections: v ?? undefined })} min={0} />
          </div>
        </>
      );
    case 'short_text':
    case 'long_text':
      return (
        <div className="grid grid-cols-2 gap-4">
          <LabeledInput label="Placeholder (optional)" value={config.placeholder ?? ''} onChange={(v) => onChange({ ...config, placeholder: v || undefined })} />
          <LabeledNumber label="Max length (optional)" value={config.maxLength ?? null} onChange={(v) => onChange({ ...config, maxLength: v ?? undefined })} min={1} />
        </div>
      );
    case 'rating_scale':
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            <LabeledNumber label="Min" value={config.min} onChange={(v) => onChange({ ...config, min: v ?? 1 })} />
            <LabeledNumber label="Max" value={config.max} onChange={(v) => onChange({ ...config, max: v ?? 10 })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <LabeledInput label="Low-end label (optional)" value={config.minLabel ?? ''} onChange={(v) => onChange({ ...config, minLabel: v || undefined })} placeholder="Not satisfied" />
            <LabeledInput label="High-end label (optional)" value={config.maxLabel ?? ''} onChange={(v) => onChange({ ...config, maxLabel: v || undefined })} placeholder="Very satisfied" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={config.allowOptionalComment ?? false} onChange={(e) => onChange({ ...config, allowOptionalComment: e.target.checked })} />
            Allow an optional comment
          </label>
        </>
      );
    case 'date':
      return (
        <div className="grid grid-cols-2 gap-4">
          <LabeledInput label="Earliest date (optional)" value={config.minDate ?? ''} onChange={(v) => onChange({ ...config, minDate: v || undefined })} placeholder="YYYY-MM-DD" />
          <LabeledInput label="Latest date (optional)" value={config.maxDate ?? ''} onChange={(v) => onChange({ ...config, maxDate: v || undefined })} placeholder="YYYY-MM-DD" />
        </div>
      );
    case 'number':
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            <LabeledNumber label="Min (optional)" value={config.min ?? null} onChange={(v) => onChange({ ...config, min: v ?? undefined })} />
            <LabeledNumber label="Max (optional)" value={config.max ?? null} onChange={(v) => onChange({ ...config, max: v ?? undefined })} />
          </div>
          <LabeledInput label="Unit (optional)" value={config.unit ?? ''} onChange={(v) => onChange({ ...config, unit: v || undefined })} placeholder="e.g. members" />
        </>
      );
    case 'name':
      return null; // handled by NameEditor (read-only)
  }
}

function AllowOtherToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      Include an “Other…” free-text option
    </label>
  );
}

function OptionsEditor({ options, onChange }: { options: ChoiceOption[]; onChange: (next: ChoiceOption[]) => void }) {
  const setLabel = (id: string, label: string) => onChange(options.map((o) => (o.id === id ? { ...o, label } : o)));
  const add = () => onChange([...options, { id: crypto.randomUUID(), label: '' }]);
  const remove = (id: string) => onChange(options.filter((o) => o.id !== id));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    const next = options.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  return (
    <div>
      <FieldLabel>Options</FieldLabel>
      <div className="space-y-2">
        {options.map((o, i) => (
          <div key={o.id} className="flex items-center gap-1">
            <input
              type="text"
              value={o.label}
              onChange={(e) => setLabel(o.id, e.target.value)}
              placeholder={`Option ${i + 1}`}
              className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
            />
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30" aria-label="Move up">
              <ArrowUp className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === options.length - 1} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30" aria-label="Move down">
              <ArrowDown className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => remove(o.id)} disabled={options.length <= 1} className="rounded p-1 text-red-400 hover:bg-red-50 disabled:opacity-30" aria-label="Remove option">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="mt-2" onClick={add}>
        + Add option
      </Button>
    </div>
  );
}
